import EntryCard from './EntryCard.jsx'
import { categorySubtotal } from './estimate.js'

// One of the four fixed rows. Collapsible, any number open at once.
//
// The subtotal in the header is the same honest arithmetic as the top bar,
// scoped to this category — it comes from the service's `by_category` roll-up
// rather than being re-derived from the cards, for the same reason the top bar
// does: one implementation of the maths, in the service, and the renderer
// renders it.
//
// ⚠️ NO DESTINATION GROUPING HERE, AT ANY COUNT. The board groups by CATEGORY
// and by nothing else. HANDOFF's hardest rule is that with zero or one
// destination "the concept is completely invisible", and T4 ships no
// destination surface at all — the compare screen and its grouping are T9,
// which appears only at two or more. A grouping level added here "just for
// later" would be the tax the rule exists to prevent, paid by every
// single-destination trip.

export default function CategoryRow({
  category,
  entries,
  rollUp,
  open,
  onToggle,
  you,
  onVote,
  onRemove,
  busy,
  // P5-3 T9. An archived place's board is a RECORD — "every entry, star, note
  // and unfurled photo survives" — and a record you can vote on is not one.
  // Passed straight through: this row makes no decision about it, and the card
  // is where "read-only" has to mean something visible.
  readOnly = false,
  // P5-3 T10. A `Map<entryId, {distance}>`, or absent. Passed straight through
  // and never inspected here — the four distance states are `EntryCard`'s to
  // render, and a row that decided anything about them would be a second place
  // for "distance unavailable" to be written.
  //
  // ⚠️ ABSENT IS THE FIRST STATE OF EVERY PAGE LOAD. Enrichment is a second
  // request (§6: *"Decoration may fail; the grid may not"*), so this row must
  // render completely before it arrives and must never wait for it.
  distances,
  // P6-82. Passed straight through, exactly like `distances` above: whether a
  // card offers "try for a preview" is a fact about that card's link and its
  // photo, and this row must not learn an opinion about either.
  onPreview,
  // The id of the entry whose preview is in flight, or null.
  previewing,
}) {
  const count = entries.length

  return (
    <section className="d2d-row" data-testid={`row-${category.key}`}>
      <h2 className="d2d-row__heading">
        <button
          type="button"
          className="d2d-row__head"
          aria-expanded={open}
          onClick={() => onToggle(category.key)}
        >
          <span className="d2d-row__numeral d2d-micro" aria-hidden="true">
            {category.numeral}
          </span>
          <span className="d2d-row__name">{category.label}</span>
          <span className="d2d-row__count d2d-micro">
            {count} {count === 1 ? 'idea' : 'ideas'}
          </span>
          <span className="d2d-row__subtotal" data-testid={`subtotal-${category.key}`}>
            {categorySubtotal(rollUp)}
          </span>
          <span className="d2d-row__toggle d2d-micro" aria-hidden="true">
            {open ? 'collapse' : 'open'}
          </span>
        </button>
      </h2>

      {open ? (
        count === 0 ? (
          <p className="d2d-row__empty">Nothing here yet.</p>
        ) : (
          <div className="d2d-row__cards">
            {entries.map((entry) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                you={you}
                onVote={onVote}
                onRemove={onRemove}
                busy={busy}
                readOnly={readOnly}
                enrichment={distances?.get(entry.id)}
                onPreview={onPreview}
                previewing={previewing === entry.id}
              />
            ))}
          </div>
        )
      ) : null}
    </section>
  )
}
